"use client"

import { useState, useRef, useEffect, useCallback } from "react"

import { Modal, ModalHeader, ModalBody, Button, Alert, Spinner, Row, Col, Card, CardBody } from "reactstrap"

import {
  FaDownload,
  FaPrint,
  FaQrcode,
  FaUser,
  FaIdCard,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaCalendarAlt,
  FaHashtag,
  FaCopy,
} from "react-icons/fa"

import { toast } from "react-toastify"

import QRCode from "qrcode"

// Create a helper function for dynamic images (replace with your actual implementation)
const dynamicImage = (imagePath: string): string => {
  // This should match your project's image loading logic
  // For now, using a simple path construction
  return `/assets/images/${imagePath}`
}

interface BadgeInfo {
  person_full_name: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  exhibition_description: string
  exhibition_venue?: string // Added venue field
  start_date: string
  end_date: string
  exhibition_phone?: string // Added exhibition contact phone
  exhibition_email?: string // Added exhibition contact email
  stand_category: string // Changed from category to stand_category
  print_type: string
  sequence_number: number
  verification_code: string
  generated_at: string
  id_doc_number?: string
  email?: string
  phone?: string
  role_color?: string // Added for database color
}

interface VerificationPayload {
  verification_code: string
}

interface BadgeData {
  badge_info: BadgeInfo
  verification_payload: VerificationPayload
  photo_url: string | null
  has_photo: boolean
}

interface TemplateConfig {
  width: number
  height: number
  colors: {
    primary: string
    secondary: string
    background: string
    text: string
  }
  fonts: {
    title: { size: number; weight: string }
    name: { size: number; weight: string }
    role: { size: number; weight: string }
    info: { size: number; weight: string }
    small: { size: number; weight: string }
  }
  layout: {
    photo_size: number
    photo_position: { x: number; y: number }
    qr_size: number
    show_qr: boolean
    show_logo: boolean
  }
}

interface Props {
  isOpen: boolean
  onToggle: () => void
  badgeData: BadgeData | null
  templateConfig?: TemplateConfig | null
  onComplete: (badgeImage: string) => void
}

const EnhancedPSFBadgeDesigner = ({ isOpen, onToggle, badgeData, templateConfig, onComplete }: Props) => {
  const canvasRef = useRef<HTMLImageElement>(null)
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null)
  const [badgeImage, setBadgeImage] = useState<string | null>(null)
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  const [photoLoaded, setPhotoLoaded] = useState(false)
  const [photoImage, setPhotoImage] = useState<HTMLImageElement | null>(null)
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null)
  const [isGenerationComplete, setIsGenerationComplete] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [exhibitionText, setExhibitionText] = useState<string>("") // Declare exhibitionText variable
  const [showLogo, setShowLogo] = useState(false)

  // Badge dimensions (portrait with increased height)
  const BADGE_WIDTH = 220
  const BADGE_HEIGHT = 280 // Increased height for portrait
  const DPI_SCALE = 2

  // PSF Colors
  const PSF_ORANGE = "#ba4a00"
  const PSF_GREEN = "#0e6655"
  const PSF_DARK_GREEN = "#16a34a"

  // Category colors based on stand_category
  const categoryColors = {
    partner: "#8B5CF6",
    platinum: "#E5E7EB",
    gold: "#F59E0B",
    silver: "#9CA3AF",
    staff: PSF_GREEN,
    // Default fallback
    exhibitor: PSF_ORANGE,
  }

  // Add PSF logo state
  const [psfLogoImage, setPsfLogoImage] = useState<HTMLImageElement | null>(null)

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBadgeImage(null)
      setQrCodeImage(null)
      setCopied(false)
      setPhotoLoaded(false)
      setPhotoImage(null)
      setBackgroundImage(null)
      setPsfLogoImage(null)
      setIsGenerationComplete(false)
      setExhibitionText("") // Reset exhibitionText when modal closes
    }
  }, [isOpen])

  // Load background image
  useEffect(() => {
    if (isOpen) {
      // Use native Image constructor for canvas operations
      const logoImg = new window.Image()
      logoImg.crossOrigin = "anonymous"
      logoImg.onload = () => {
        setPsfLogoImage(logoImg)
      }
      logoImg.onerror = () => {
        console.warn("PSF logo failed to load")
      }
      // Use dynamicImage function to get the proper logo path
      logoImg.src = dynamicImage("logo.png")

      const bgImg = new window.Image()
      bgImg.crossOrigin = "anonymous"
      bgImg.onload = () => {
        setBackgroundImage(bgImg)
      }
      bgImg.onerror = () => {
        console.warn("Background image failed to load")
      }
      bgImg.src = "/assets/images/convention.png"
    }
  }, [isOpen])

  // Generate high-quality QR code
  useEffect(() => {
    if (isOpen && badgeData) {
      // Only include verification code in QR
      const qrData = badgeData.badge_info.verification_code

      QRCode.toDataURL(qrData, {
        width: 100 * DPI_SCALE, // Appropriate size for portrait
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
        errorCorrectionLevel: "H",
      })
        .then(setQrCodeImage)
        .catch((error) => {
          console.error("QR Code generation failed:", error)
          toast.error("QR Code generation failed")
        })
    }
  }, [badgeData, isOpen])

  useEffect(() => {
    if (shouldClose && isGenerationComplete) {
      onToggle()
      setShouldClose(false)
    }
  }, [shouldClose, isGenerationComplete, onToggle])

  // Load photo with higher quality
  useEffect(() => {
    if (isOpen && badgeData?.has_photo && badgeData.photo_url) {
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        setPhotoImage(img)
        setPhotoLoaded(true)
      }
      img.onerror = () => {
        setPhotoLoaded(true)
        console.error("Error loading photo")
      }
      img.src = badgeData.photo_url
    } else if (badgeData) {
      setPhotoLoaded(true)
    }
  }, [badgeData, isOpen])

  // Helper function to draw rounded rectangle with specific corners
  const drawRoundedRectTopOnly = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height)
    ctx.lineTo(x, y + height)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  // Helper function to draw fully rounded rectangle
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  // Helper function to wrap text to multiple lines
  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    textAlign: "left" | "center" | "right" = "left",
  ) => {
    const words = text.split(" ")
    let line = ""
    const currentY = y
    const lines: string[] = []

    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + " "
      const metrics = ctx.measureText(testLine)
      const testWidth = metrics.width

      if (testWidth > maxWidth && n > 0) {
        lines.push(line.trim())
        line = words[n] + " "
      } else {
        line = testLine
      }
    }
    lines.push(line.trim())

    // Draw each line
    lines.forEach((lineText, index) => {
      ctx.textAlign = textAlign
      ctx.fillText(lineText, x, currentY + index * lineHeight)
    })

    return lines.length * lineHeight // Return total height used
  }

  // Enhanced badge generation matching crown design exactly
  const generateBadge = useCallback(() => {
    if (!badgeData || !hiddenCanvasRef.current) return

    setIsRendering(true)

    const canvas = hiddenCanvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    try {
      // Set high-resolution dimensions
      canvas.width = BADGE_WIDTH * DPI_SCALE
      canvas.height = BADGE_HEIGHT * DPI_SCALE

      // Scale context for high DPI
      ctx.scale(DPI_SCALE, DPI_SCALE)

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = "high"

      // Clear canvas
      ctx.clearRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT)

      // Mixed green/black color for crown design
      const mixedGreenBlack = "#212f3d" // Dark green-black mix

      // 1. BACKGROUND - Cover entire badge with mixed green/black
      ctx.fillStyle = mixedGreenBlack
      ctx.fillRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT)

      // Section heights - made smaller to fit better
      const headerHeight = 30 // Reduced further
      const whiteHeight = 67 // Reduced
      const greenHeight = 100 // Reduced

      // 2. Header section - Split design following crown example
      // Left part - mixed green/black background (already applied as base)
      const leftHeaderWidth = BADGE_WIDTH / 2 - 16 // Space for cable dot, made smaller

      // Right part – white background with bottom‑left corner rounded
      const x = BADGE_WIDTH / 2 + 4;
      const y = 0;
      const w = BADGE_WIDTH / 2 - 4;
      const h = headerHeight;
      const radius = 10;  // adjust to taste

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      // top‑left
      ctx.moveTo(x, y);
      // top edge
      ctx.lineTo(x + w, y);
      // right edge
      ctx.lineTo(x + w, y + h);
      // bottom edge, stopping radius in from left
      ctx.lineTo(x + radius, y + h);
      // quarter‑circle into left edge
      ctx.arcTo(x, y + h, x, y + h - radius, radius);
      // left edge back to top
      ctx.lineTo(x, y);
      // finish
      ctx.closePath();
      ctx.fill();

      // Left side – PSF website (on mixed green/black background)
      ctx.fillStyle = "#ffffff";
      ctx.font = " 10px Arial"; // Made smaller
      ctx.textAlign = "left";
      ctx.fillText("www.psf.org.rw", 20, 17); // Adjusted position

      

      // Right side – Exhibition title and description (on white background)
      const textX = BADGE_WIDTH / 2 + 17;  
      ctx.fillStyle = "#000000";          

      // Title, left‑aligned
      ctx.textAlign = "left";
      ctx.font = " bold 10px Arial";       // Made smaller
      ctx.fillText(
        badgeData.badge_info.exhibition_title.toUpperCase(),
        textX,
        17
      );

      // Description with intelligent wrapping, left‑aligned
      // ctx.font = "bold 6px Arial";       
      // ctx.textAlign = "left";
      // const exhibitionDesc = badgeData.badge_info.exhibition_description.toLowerCase();
      // const maxWidth = BADGE_WIDTH / 2 - 16; // panel width minus left/right padding (4+12)
      // const words = exhibitionDesc.split(" ");
      // let line = "";
      // let yText = 23;

      // for (let n = 0; n < words.length; n++) {
      //   const testLine = line + words[n] + " ";
      //   const testWidth = ctx.measureText(testLine).width;

      //   if (testWidth > maxWidth && n > 0) {
      //     ctx.fillText(line, textX, yText);
      //     line = words[n] + " ";
      //     yText += 10; // Reduced line height
      //   } else {
      //     line = testLine;
      //   }
      // }
      // // last line
      // ctx.fillText(line, textX, yText);


      // 3. White section - Profile image area (layered over mixed background)
      const whiteAreaHeight = 70 // Reduced to make room for role
      ctx.fillStyle = mixedGreenBlack
      ctx.fillRect(0, headerHeight, BADGE_WIDTH / 2 + 40, whiteAreaHeight) // Only covers part of the width

      // Profile image with rounded corners and green shadow border
      const photoWidth = 70 // Made smaller
      const photoHeight = 80 // Made smaller
      const photoX = (BADGE_WIDTH - photoWidth) / 2
      const photoY = headerHeight + 30 // Adjusted position

      // Green shadow border (larger rounded rectangle)
      ctx.save()
      ctx.shadowColor = "rgba(254, 250, 250, 0.1)"
      ctx.shadowBlur = 5
      ctx.shadowOffsetY = 2
      drawRoundedRect(ctx, photoX - 4, photoY - 4, photoWidth + 8, photoHeight + 8, 15)
      ctx.fillStyle = "rgb(254, 250, 250)"
      ctx.fill()
      ctx.restore()

      // Photo area with rounded corners (layered over everything)
      ctx.save()
      drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, 12)
      ctx.fillStyle = "#f8f9fa"
      ctx.fill()
      ctx.clip()

      // Draw photo or placeholder
      if (photoImage) {
        const scale = Math.max(photoWidth / photoImage.width, photoHeight / photoImage.height)
        const scaledWidth = photoImage.width * scale
        const scaledHeight = photoImage.height * scale
        const centerX = photoX + photoWidth / 2
        const centerY = photoY + photoHeight / 2
        const drawX = centerX - scaledWidth / 2
        const drawY = centerY - scaledHeight / 2

        ctx.drawImage(photoImage, drawX, drawY, scaledWidth, scaledHeight)
      } else {
        // Photo placeholder
        ctx.fillStyle = "#6b7280"
        ctx.font = "12px Arial" // Made smaller
        ctx.textAlign = "center"
        ctx.fillText("PHOTO", photoX + photoWidth / 2, photoY + photoHeight / 2 - 8)
        ctx.fillText("PLACEHOLDER", photoX + photoWidth / 2, photoY + photoHeight / 2 + 8)
      }

      ctx.restore()

      // 4. Role section - positioned under photo, with curved top corners (layered over mixed background)
      const roleY = photoY + photoHeight + 40; // Adjusted position
      const roleWidth = BADGE_WIDTH * 0.83;    // Made smaller to fit better
      const roleX = 19;                        // Adjusted position
      const greenY = headerHeight + whiteHeight;

      // Determine role color - check database color first, fallback to orange
      const displayRole = badgeData.badge_info.role || badgeData.badge_info.stand_category;
      let roleColor = PSF_ORANGE; // Default fallback

      // Check if role_color exists from database
      if (badgeData.badge_info.role_color) {
        roleColor = badgeData.badge_info.role_color;
      }

      // Draw role rectangle - rounded top corners only, limited width (layered over mixed background)
      const roleHeight = 38; // Made smaller
      ctx.save();
      drawRoundedRectTopOnly(ctx, roleX, roleY, roleWidth, roleHeight, 12);
      ctx.fillStyle = roleColor;
      ctx.fill();
      ctx.restore();

      // Role text
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 16px Arial"; // Made smaller
      ctx.textAlign = "center";

      // Handle long role text
      let roleText = displayRole;
      if (roleText.length > 15) {
        ctx.font = "bold 12px Arial"; // Made smaller
        if (roleText.length > 20) {
          roleText = roleText.substring(0, 17) + "...";
        }
      }

      ctx.fillText(roleText, roleX + roleWidth / 2, roleY + 25); // Adjusted position

      
    // extend role-color down under the green's rounded corner so white never shows
    ctx.save()
    ctx.fillStyle = roleColor
    // this square is exactly the 12×12px quarter‑circle hole in your green
     ctx.fillRect(
       roleX + roleWidth - 12,
       roleY   + roleHeight,
       12,
      12
    )
     ctx.restore()
     // ───

      // 5. Green section - Limited width, with curved top-right corner matching role (layered over mixed background)
      const adjustedGreenY = roleY + roleHeight;
      const greenWidth = roleWidth + 19; // Slightly wider than role
     
      // Draw green section with only top‑right rounded
      ctx.save();
      ctx.fillStyle = PSF_GREEN;
      ctx.beginPath();
      // bottom‑left
      ctx.moveTo(0, adjustedGreenY + greenHeight);
      // up left edge
      ctx.lineTo(0, adjustedGreenY);
      // across top, stopping radius before right corner
      ctx.lineTo(greenWidth - 12, adjustedGreenY);
      // arc into right edge
      ctx.arcTo(greenWidth, adjustedGreenY, greenWidth, adjustedGreenY + 12, 12);
      // down right edge
      ctx.lineTo(greenWidth, adjustedGreenY + greenHeight);
      // back along bottom
      ctx.lineTo(0, adjustedGreenY + greenHeight);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Bottom section layout based on logo presence
      const bottomY = adjustedGreenY + 60; // Adjusted position

      // showLogo is already defined in state
      if (showLogo && psfLogoImage) {
        // Version 2: Logo (left) + Staff info (center) + QR (right)
        // PSF Logo on the left (within green section, layered over everything)
        const logoSize = 40 // Made smaller
        const logoX = 15 // Adjusted position
        const logoY = bottomY - 50 // Adjusted position

        // Logo background (layered)
        ctx.fillStyle = "#ffffff"
        ctx.fillRect(logoX - 2, logoY - 2, logoSize + 4, logoSize + 4)
        ctx.drawImage(psfLogoImage, logoX, logoY, logoSize, logoSize)

        // Staff info in center (within green section, layered)
        ctx.fillStyle = "#ffffff"
        ctx.font = "bold 9px Arial" // Made smaller
        const centerX = greenWidth / 2
        const maxNameWidth = 100 // Adjusted for smaller size

        // Wrap participant name
        const nameHeight = wrapText(
          ctx,
          badgeData.badge_info.person_full_name,
          centerX,
          bottomY - 40, // Adjusted position
          maxNameWidth,
          10, // Reduced line height
          "center",
        )

        ctx.font = " 9px Arial" // Made smaller
        const maxCompanyWidth = 100

        // Wrap company name (position after name)
        const companyHeight = wrapText(
          ctx,
          badgeData.badge_info.exhibitor_name,
          centerX,
          bottomY - 30 + nameHeight - 7, // Adjusted spacing
          maxCompanyWidth,
          12, // Reduced line height
          "center",
        )

        // QR Code with shadow and rounded corners (within green section, layered)
        if (qrCodeImage) {
          const qrImg = new window.Image()
          qrImg.src = qrCodeImage
          qrImg.onload = () => {
            const qrSize = 40 // Made smaller
            const qrX = greenWidth - qrSize - 10 // Adjusted position
            const qrY = bottomY - 50 // Adjusted position

            // QR shadow
            ctx.save()
            ctx.shadowColor = PSF_GREEN
            ctx.shadowBlur = 6
            ctx.shadowOffsetX = 2
            ctx.shadowOffsetY = 3

            // QR background with rounded corners (layered)
            drawRoundedRect(ctx, qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 6)
            ctx.fillStyle = PSF_GREEN
            ctx.fill()
            ctx.restore()

            // QR code image with rounded corners (layered)
            ctx.save()
            drawRoundedRect(ctx, qrX, qrY, qrSize, qrSize, 4)
            ctx.clip()
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize)
            ctx.restore()

            // Generate final badge image
            const dataURL = canvas.toDataURL("image/png", 1.0)
            setBadgeImage(dataURL)
            onComplete(dataURL)
            setIsGenerationComplete(true)
            setIsRendering(false)
          }
        }
      } else {
        // Version 1: Staff info (left) + QR (right) - matching crown design
        // Staff info on the left (within green section, layered)
        ctx.fillStyle = "#ffffff"
        ctx.font = "bold 8px Arial" // Made smaller
        const maxNameWidth = 120 // Adjusted for smaller size

        // Wrap participant name
        const nameHeight = wrapText(
          ctx,
          badgeData.badge_info.person_full_name,
          21, // Adjusted position
          bottomY - 40, // Adjusted position
          maxNameWidth,
          10, // Reduced line height
          "left",
        )

        ctx.font = "8px Arial" // Made smaller
        const maxCompanyWidth = 150

        // Wrap company name (position after name)
        const companyHeight = wrapText(
          ctx,
          badgeData.badge_info.exhibitor_name,
          21, // Adjusted position
          bottomY - 30 + nameHeight - 7, // Adjusted spacing
          maxCompanyWidth,
          13, // Reduced line height
          "left",
        )

        // QR Code with shadow and rounded corners (within green section, layered)
        if (qrCodeImage) {
          const qrImg = new window.Image()
          qrImg.src = qrCodeImage
          qrImg.onload = () => {
            const qrSize = 40 // Made smaller
            const qrX = greenWidth - qrSize - 10 // Adjusted position
            const qrY = bottomY - 50 // Adjusted position

            // QR shadow
            ctx.save()
            ctx.shadowColor =PSF_GREEN
            ctx.shadowBlur = 6
            ctx.shadowOffsetX = 2
            ctx.shadowOffsetY = 3

            // QR background with rounded corners (layered)
            drawRoundedRect(ctx, qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 6)
            ctx.fillStyle = PSF_GREEN
            ctx.fill()
            ctx.restore()

            // QR code image with rounded corners (layered)
            ctx.save()
            drawRoundedRect(ctx, qrX, qrY, qrSize, qrSize, 4)
            ctx.clip()
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize)
            ctx.restore()

            // Generate final badge image
            const dataURL = canvas.toDataURL("image/png", 1.0)
            setBadgeImage(dataURL)
            onComplete(dataURL)
            setIsGenerationComplete(true)
            setIsRendering(false)
          }
        }
      }

      // If no QR code, still complete the badge
      if (!qrCodeImage) {
        const dataURL = canvas.toDataURL("image/png", 1.0)
        setBadgeImage(dataURL)
        onComplete(dataURL)
        setIsGenerationComplete(true)
        setIsRendering(false)
      }
    } catch (error) {
      console.error("Badge generation failed:", error)
      toast.error("Failed to generate badge")
      setIsRendering(false)
    }
  }, [badgeData, qrCodeImage, photoImage, backgroundImage, onComplete, templateConfig, psfLogoImage, showLogo])

  // Generate badge when all components are ready
  useEffect(() => {
    if (isOpen && badgeData && photoLoaded) {
      setExhibitionText(badgeData.badge_info.exhibition_title) // Set exhibitionText before generating badge
      generateBadge()
    }
  }, [isOpen, badgeData, photoLoaded, generateBadge, showLogo])

  const downloadBadge = () => {
    if (badgeImage && badgeData) {
      const link = document.createElement("a")
      link.href = badgeImage
      link.download = `psf-badge-${badgeData.badge_info.person_full_name.replace(/\s+/g, "-")}-${badgeData.badge_info.sequence_number.toString().padStart(4, "0")}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success("Crown-style PSF Badge downloaded successfully!")
    }
  }

  const printBadge = () => {
    if (badgeImage && badgeData) {
      const printWindow = window.open("", "_blank")
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Crown-style PSF Badge Print - ${badgeData.badge_info.person_full_name}</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  text-align: center; 
                  font-family: Arial, sans-serif; 
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  min-height: 100vh;
                }
                
                .print-container {
                  max-width: 500px;
                  margin: 0 auto;
                  background: white;
                  border-radius: 15px;
                  padding: 30px;
                  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }
                
                .badge-container { 
                  display: inline-block; 
                  margin: 20px auto;
                  border-radius: 12px;
                  overflow: hidden;
                  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                }
                
                img { 
                  width: 400px;
                  height: 600px;
                  display: block;
                }
                
                .print-info { 
                  margin-top: 25px; 
                  font-size: 16px; 
                  color: #2d3748;
                  padding: 20px;
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  color: white;
                  border-radius: 10px;
                  text-align: left;
                }
                
                @media print { 
                  body { 
                    padding: 0; 
                    background: white;
                  }
                  .print-container {
                    box-shadow: none;
                    max-width: none;
                  }
                }
              </style>
            </head>
            <body>
              <div class="print-container">
                <h1 style="color: #ff6b35;">Crown-style PSF Rwanda Exhibition Badge</h1>
                <p style="color: #22c55e; font-weight: bold;">Private Sector Federation - Rwanda</p>
                
                <div class="badge-container">
                  <img src="${badgeImage}" alt="Crown-style PSF Badge" />
                </div>
                
                <div class="print-info">
                  <h3>Badge Information</h3>
                  <p><strong>Name:</strong> ${badgeData.badge_info.person_full_name}</p>
                  <p><strong>Role:</strong> ${badgeData.badge_info.role}</p>
                  <p><strong>Company:</strong> ${badgeData.badge_info.exhibitor_name}</p>
                  <p><strong>Exhibition:</strong> ${badgeData.badge_info.exhibition_title}</p>
                  <p><strong>Stand Category:</strong> ${badgeData.badge_info.stand_category}</p>
                  ${badgeData.badge_info.exhibition_venue ? `<p><strong>Venue:</strong> ${badgeData.badge_info.exhibition_venue}</p>` : ""}
                  <p><strong>Dates:</strong> ${badgeData.badge_info.start_date} - ${badgeData.badge_info.end_date}</p>
                  <p><strong>Verification:</strong> ${badgeData.badge_info.verification_code}</p>
                  <p><em>✨ Crown-style design matching reference exactly</em></p>
                </div>
              </div>
              
              <script>
                window.onload = function() {
                  setTimeout(() => {
                    window.print();
                    setTimeout(() => window.close(), 1000);
                  }, 500);
                }
              </script>
            </body>
          </html>
        `)
        printWindow.document.close()
      }
    }
  }

  const copyVerificationCode = () => {
    if (badgeData?.badge_info.verification_code) {
      navigator.clipboard
        .writeText(badgeData.badge_info.verification_code)
        .then(() => {
          setCopied(true)
          toast.success("Verification code copied to clipboard!")
          setTimeout(() => setCopied(false), 3000)
        })
        .catch((err) => {
          console.error("Failed to copy: ", err)
          toast.error("Failed to copy verification code")
        })
    }
  }

  const viewQRCode = () => {
    if (qrCodeImage && badgeData) {
      const qrWindow = window.open("", "_blank")
      if (qrWindow) {
        qrWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>PSF QR Verification - ${badgeData.badge_info.person_full_name}</title>
              <style>
                body { 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh; 
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  margin: 0; 
                  font-family: Arial, sans-serif;
                  padding: 20px;
                }
                
                .qr-container { 
                  text-align: center; 
                  padding: 40px; 
                  background: white; 
                  border-radius: 20px; 
                  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                  max-width: 500px;
                  width: 100%;
                }
                
                h1 { 
                  color: #ff6b35; 
                  margin-bottom: 10px;
                  font-weight: bold;
                  font-size: 28px;
                }
                
                .subtitle {
                  color: #22c55e;
                  margin-bottom: 30px;
                  font-size: 16px;
                  font-weight: bold;
                }
                
                .qr-image {
                  border: 3px solid #22c55e;
                  border-radius: 15px;
                  margin: 20px 0;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                
                .info-section {
                  background: linear-gradient(135deg, #fff5f0 0%, #f0fdf4 100%);
                  border-radius: 15px;
                  padding: 25px;
                  margin-top: 25px;
                  text-align: left;
                }
                
                .verification-highlight {
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  color: white;
                  padding: 15px;
                  border-radius: 10px;
                  margin-top: 20px;
                  font-weight: bold;
                  text-align: center;
                }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <h1>🔒 Crown-style PSF QR Verification</h1>
                <p class="subtitle">Private Sector Federation - Rwanda</p>
                
                <img src="${qrCodeImage}" alt="Verification QR Code" width="220" height="220" class="qr-image" />
                
                <div class="info-section">
                  <h3 style="color: #ff6b35;">Badge Information</h3>
                  <p><strong>Name:</strong> ${badgeData.badge_info.person_full_name}</p>
                  <p><strong>Role:</strong> ${badgeData.badge_info.role}</p>
                  <p><strong>Company:</strong> ${badgeData.badge_info.exhibitor_name}</p>
                  <p><strong>Exhibition:</strong> ${badgeData.badge_info.exhibition_title}</p>
                  <p><strong>Stand Category:</strong> ${badgeData.badge_info.stand_category}</p>
                  ${badgeData.badge_info.exhibition_venue ? `<p><strong>Venue:</strong> ${badgeData.badge_info.exhibition_venue}</p>` : ""}
                  
                  <div class="verification-highlight">
                    🔐 Verification Code: ${badgeData.badge_info.verification_code}
                  </div>
                </div>
              </div>
            </body>
          </html>
        `)
        qrWindow.document.close()
      }
    }
  }

  return (
    <>
      {/* Hidden high-resolution canvas for generation */}
      <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />

      <Modal isOpen={isOpen} toggle={onToggle} size="xl" centered className="enhanced-psf-badge-designer-modal">
        <ModalHeader
          toggle={onToggle}
          className="text-white border-0"
          style={{ background: "linear-gradient(135deg, #ff6b35 0%, #22c55e 100%)" }}
        >
          <div className="d-flex align-items-center">
            <FaIdCard className="me-3" size={24} />
            <div>
              <h4 className="mb-0">✨ Crown-style PSF Rwanda Badge Designer</h4>
              {badgeData && (
                <small className="opacity-75">
                  {badgeData.badge_info.person_full_name} • {badgeData.badge_info.print_type.toUpperCase()} • Crown
                  Design with Connected Role Section
                </small>
              )}
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="p-0" style={{ background: "linear-gradient(135deg, #fff5f0 0%, #f0fdf4 100%)" }}>
          {badgeData ? (
            <div>
              <Row className="g-0">
                <Col lg={7} className="p-4">
                  <div className="position-relative">
                    <div className="text-center">
                      {badgeImage ? (
                        <img
                          ref={canvasRef}
                          src={badgeImage || "/placeholder.svg"}
                          alt="Crown-style PSF Badge"
                          className="img-fluid shadow-lg"
                          style={{
                            maxWidth: "400px",
                            maxHeight: "600px", // Portrait orientation
                            borderRadius: "12px",
                            border: "3px solid #ffffff",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex flex-column justify-content-center align-items-center bg-white shadow-lg"
                          style={{
                            width: "400px",
                            height: "600px", // Portrait orientation
                            borderRadius: "12px",
                            border: "3px solid #ff6b35",
                            margin: "0 auto",
                          }}
                        >
                          <Spinner style={{ color: "#ff6b35" }} size="lg" />
                          <p className="mt-3 text-muted">Generating crown-style PSF badge...</p>
                          <small className="text-muted">Matching crown design with connected sections</small>
                        </div>
                      )}
                    </div>

                    {isRendering && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                        style={{
                          background: "rgba(255, 255, 255, 0.95)",
                          borderRadius: "12px",
                          backdropFilter: "blur(5px)",
                        }}
                      >
                        <Spinner style={{ color: "#ff6b35" }} size="lg" />
                        <p className="mt-3 text-dark fw-medium">Rendering crown-style PSF badge...</p>
                        <small className="text-muted">✨ Crown design with connected sections</small>
                      </div>
                    )}
                  </div>
                </Col>

                <Col lg={5} className="bg-white border-start">
                  <div className="p-4 h-100">
                    <div className="d-flex align-items-center mb-4">
                      <div className="rounded-circle p-2 me-3" style={{ backgroundColor: "rgba(255, 107, 53, 0.1)" }}>
                        <FaUser style={{ color: "#ff6b35" }} size={20} />
                      </div>
                      <h5 className="mb-0 fw-bold text-dark">✨ Crown-style PSF Badge</h5>
                    </div>

                    <div className="space-y-4">
                      {/* Enhancement Features */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #8b5cf6" }}>
                        <CardBody className="p-3">
                          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: "#8b5cf6" }}>
                            ✨ Crown Design Features
                          </h6>
                          <div className="space-y-2">
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">✓</span>
                              <span className="small">Split header with mixed green/black left side</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">✓</span>
                              <span className="small">White right header with exhibition details</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">✓</span>
                              <span className="small">Left extension with mixed green/black background</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">✓</span>
                              <span className="small">Role section limited width (doesn't reach right border)</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">✓</span>
                              <span className="small">Green section stops where role stops</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">✓</span>
                              <span className="small">Right extensions with mixed green/black background</span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      {/* Logo Control */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #f59e0b" }}>
                        <CardBody className="p-3">
                          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: "#f59e0b" }}>
                            🎛️ Badge Configuration
                          </h6>
                          <div className="space-y-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <span className="fw-medium">PSF Logo</span>
                                <br />
                                <small className="text-muted">Include PSF logo in green section</small>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="logoToggle"
                                  checked={showLogo}
                                  onChange={(e) => setShowLogo(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="logoToggle">
                                  {showLogo ? "Enabled" : "Disabled"}
                                </label>
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className={`badge ${showLogo ? "bg-success" : "bg-secondary"} me-2`}>
                                {showLogo ? "✓" : "○"}
                              </span>
                              <span className="small">
                                {showLogo ? "Version 2: With PSF logo layout" : "Version 1: Clean layout without logo"}
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      {/* Personal Details */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #ff6b35" }}>
                        <CardBody className="p-3">
                          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: "#ff6b35" }}>
                            <FaUser className="me-2" />
                            Personal Details
                          </h6>
                          <div className="space-y-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Name:</span>
                              <span className="fw-medium">{badgeData.badge_info.person_full_name}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Role:</span>
                              <span className="fw-medium">{badgeData.badge_info.role}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Stand Category:</span>
                              <span className="fw-medium">{badgeData.badge_info.stand_category}</span>
                            </div>
                            {badgeData.badge_info.role_color && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Role Color:</span>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="rounded me-2"
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      backgroundColor: badgeData.badge_info.role_color,
                                    }}
                                  />
                                  <span className="fw-medium small">{badgeData.badge_info.role_color}</span>
                                </div>
                              </div>
                            )}
                            {badgeData.badge_info.id_doc_number && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">
                                  <FaHashtag className="me-1" size={12} />
                                  ID:
                                </span>
                                <span className="fw-medium">{badgeData.badge_info.id_doc_number}</span>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>

                      {/* Contact Information */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #22c55e" }}>
                        <CardBody className="p-3">
                          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: "#22c55e" }}>
                            <FaBuilding className="me-2" />
                            Contact & Company
                          </h6>
                          <div className="space-y-2">
                            {badgeData.badge_info.exhibition_email && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">
                                  <FaEnvelope className="me-1" size={12} />
                                  Exhibition Email:
                                </span>
                                <span className="fw-medium small">{badgeData.badge_info.exhibition_email}</span>
                              </div>
                            )}
                            {badgeData.badge_info.exhibition_phone && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">
                                  <FaPhone className="me-1" size={12} />
                                  Exhibition Phone:
                                </span>
                                <span className="fw-medium">{badgeData.badge_info.exhibition_phone}</span>
                              </div>
                            )}
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Company:</span>
                              <span className="fw-medium">{badgeData.badge_info.exhibitor_name}</span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      {/* Event Details */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #3b82f6" }}>
                        <CardBody className="p-3">
                          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: "#3b82f6" }}>
                            <FaCalendarAlt className="me-2" />
                            Event & Verification
                          </h6>
                          <div className="space-y-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Exhibition:</span>
                              <span className="fw-medium small">{badgeData.badge_info.exhibition_title}</span>
                            </div>
                            {badgeData.badge_info.exhibition_venue && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Venue:</span>
                                <span className="fw-medium small">{badgeData.badge_info.exhibition_venue}</span>
                              </div>
                            )}
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Dates:</span>
                              <span className="fw-medium">
                                {badgeData.badge_info.start_date} - {badgeData.badge_info.end_date}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Badge #:</span>
                              <span className="fw-medium">
                                {badgeData.badge_info.sequence_number.toString().padStart(4, "0")}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted">Verification:</span>
                              <div className="d-flex align-items-center">
                                <code className="bg-light px-2 py-1 rounded small me-2">
                                  {badgeData.badge_info.verification_code}
                                </code>
                                <Button
                                  size="sm"
                                  color="link"
                                  className="p-0"
                                  onClick={copyVerificationCode}
                                  title="Copy verification code"
                                >
                                  <FaCopy size={12} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Action buttons */}
              <div className="border-top bg-white p-4">
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <Button
                    onClick={printBadge}
                    className="d-flex align-items-center px-4 py-2 fw-medium"
                    style={{
                      background: "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)",
                      border: "none",
                    }}
                    disabled={!badgeImage}
                  >
                    <FaPrint className="me-2" />
                    Print Crown Badge
                  </Button>

                  <Button
                    onClick={downloadBadge}
                    className="d-flex align-items-center px-4 py-2 fw-medium"
                    style={{
                      background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                      border: "none",
                    }}
                    disabled={!badgeImage}
                  >
                    <FaDownload className="me-2" />
                    Download Crown PNG
                  </Button>

                  <Button
                    onClick={viewQRCode}
                    className="d-flex align-items-center px-4 py-2 fw-medium"
                    disabled={!qrCodeImage}
                    style={{
                      background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                      border: "none",
                    }}
                  >
                    <FaQrcode className="me-2" />
                    View QR Details
                  </Button>

                  <Button
                    onClick={() => setShouldClose(true)}
                    className="d-flex align-items-center px-4 py-2 fw-medium"
                    style={{
                      background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                      border: "none",
                    }}
                    disabled={!isGenerationComplete}
                  >
                    <FaCheck className="me-2" />
                    Complete & Close
                  </Button>
                </div>
              </div>

              {/* QR Code Information */}
              {qrCodeImage && (
                <div className="border-top p-4" style={{ backgroundColor: "#f8f9fa" }}>
                  <Row className="align-items-center">
                    <Col md={3} className="text-center">
                      <img
                        src={qrCodeImage || "/placeholder.svg"}
                        alt="QR Code"
                        style={{ width: "120px", height: "120px" }}
                        className="border rounded"
                      />
                    </Col>
                    <Col md={9}>
                      <h6 className="fw-bold text-dark mb-2">
                        <FaQrcode className="me-2" style={{ color: "#3b82f6" }} />
                        Crown-style PSF QR Code Verification
                      </h6>
                      <p className="text-muted mb-2">
                        This QR code contains verification information for crown-style PSF Rwanda badge authentication:
                      </p>
                      <div className="row">
                        <div className="col-6">
                          <ul className="list-unstyled small text-muted mb-0">
                            <li>✓ Verification code</li>
                            <li>✓ Security validation</li>
                            <li>✨ Crown design matching</li>
                          </ul>
                        </div>
                        <div className="col-6">
                          <ul className="list-unstyled small text-muted mb-0">
                            <li>✓ Badge authenticity</li>
                            <li>✓ PSF certification</li>
                            <li>✨ Connected role section</li>
                          </ul>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {!badgeImage && !isRendering && (
                <Alert color="warning" className="m-4 text-center">
                  <FaExclamationTriangle className="me-2" />
                  <strong>Preparing crown-style PSF badge generation...</strong>
                  <br />
                  <small>Loading resources and preparing crown design with connected sections</small>
                </Alert>
              )}

              {copied && (
                <Alert color="success" className="m-4 text-center">
                  <FaCheck className="me-2" />
                  <strong>Verification code copied to clipboard!</strong>
                </Alert>
              )}
            </div>
          ) : (
            <div className="p-5 text-center">
              <Alert color="danger" className="border-0 shadow-sm">
                <div className="d-flex flex-column align-items-center">
                  <FaTimes size={48} className="text-danger mb-3" />
                  <h4 className="text-danger fw-bold">No PSF Badge Data Available</h4>
                  <p className="mb-0 text-muted">
                    Please provide valid badge information to generate a crown-style PSF Rwanda badge.
                  </p>
                </div>
              </Alert>
            </div>
          )}
        </ModalBody>
      </Modal>

      <style>{`
        .enhanced-psf-badge-designer-modal .modal-dialog {
          max-width: 1200px;
        }
        .space-y-4 > * + * {
          margin-top: 1rem;
        }
        .space-y-2 > * + * {
          margin-top: 0.5rem;
        }
      `}</style>
    </>
  )
}

export default EnhancedPSFBadgeDesigner
